import { VFXEffectConfig } from '../VFXEffectRegistry';

// =============================================================================
// PARTICLE PRESETS — Themed VFXEffectConfigs for reuse across any ability
// Each preset uses category: 'particle_preset' for registry queries.
// =============================================================================

const FIRE_TRAIL: VFXEffectConfig = {
  id: 'preset_fire_trail',
  name: 'Fire Trail',
  category: 'particle_preset',
  description: 'Orange/red upward-drifting fire particles',
  primitives: [
    {
      type: 'circle',
      radius: 6,
      color: 0xFF4500,
      alpha: 0.8,
      depth: 94,
      tween: {
        y: -25,
        alpha: 0,
        scale: 0.3,
        duration: 400,
        ease: 'Power2',
      },
      scatter: { count: 8, radius: 20 },
    },
    {
      type: 'circle',
      radius: 3,
      color: 0xFFDD44,
      alpha: 0.9,
      depth: 95,
      tween: {
        y: -15,
        alpha: 0,
        scale: 0.5,
        duration: 300,
        ease: 'Power1',
      },
      scatter: { count: 5, radius: 12 },
    },
    {
      type: 'circle',
      radius: 12,
      color: 0xFF0000,
      alpha: 0.2,
      depth: 92,
      tween: {
        alpha: 0,
        scale: 1.5,
        duration: 500,
        ease: 'Power2',
      },
    },
  ],
};

const FROST_MIST: VFXEffectConfig = {
  id: 'preset_frost_mist',
  name: 'Frost Mist',
  category: 'particle_preset',
  description: 'Cyan/white slow-drifting ice crystals',
  primitives: [
    {
      type: 'star',
      points: 6,
      innerRadius: 2,
      outerRadius: 5,
      color: 0x00FFFF,
      alpha: 0.7,
      depth: 94,
      tween: {
        x: 15,
        alpha: 0,
        rotation: Math.PI * 0.5,
        scale: 0.6,
        duration: 800,
        ease: 'Sine.easeOut',
      },
      scatter: { count: 6, radius: 25 },
    },
    {
      type: 'circle',
      radius: 8,
      color: 0xADD8E6,
      alpha: 0.3,
      depth: 92,
      tween: {
        alpha: 0,
        scale: 2,
        duration: 700,
        ease: 'Power2',
      },
    },
  ],
};

const HOLY_RADIANCE: VFXEffectConfig = {
  id: 'preset_holy_radiance',
  name: 'Holy Radiance',
  category: 'particle_preset',
  description: 'Gold/white sparkles with upward scatter',
  primitives: [
    {
      type: 'star',
      points: 4,
      innerRadius: 2,
      outerRadius: 6,
      color: 0xFFD700,
      alpha: 0.9,
      depth: 95,
      tween: {
        y: -20,
        alpha: 0,
        rotation: Math.PI * 0.25,
        scale: 0.4,
        duration: 500,
        ease: 'Power2',
      },
      scatter: { count: 8, radius: 30 },
    },
    {
      type: 'circle',
      radius: 15,
      color: 0xFFFFFF,
      alpha: 0.4,
      depth: 93,
      tween: {
        alpha: 0,
        scale: 2,
        duration: 400,
        ease: 'Power1',
      },
    },
  ],
};

const SHADOW_WISPS: VFXEffectConfig = {
  id: 'preset_shadow_wisps',
  name: 'Shadow Wisps',
  category: 'particle_preset',
  description: 'Dark purple wisps orbiting inward',
  primitives: [
    {
      type: 'ellipse',
      width: 10,
      height: 5,
      color: 0x8800FF,
      alpha: 0.5,
      depth: 93,
      orbit: {
        radius: 30,
        speed: 1.2,
        orientToPath: true,
      },
      tween: {
        alpha: 0,
        scale: 0.5,
        duration: 1000,
        ease: 'Power2',
      },
      scatter: { count: 5, radius: 35 },
    },
    {
      type: 'circle',
      radius: 4,
      color: 0x330066,
      alpha: 0.6,
      depth: 94,
      tween: {
        alpha: 0,
        duration: 800,
        ease: 'Power2',
      },
      scatter: { count: 3, radius: 20 },
    },
  ],
};

const POISON_CLOUD: VFXEffectConfig = {
  id: 'preset_poison_cloud',
  name: 'Poison Cloud',
  category: 'particle_preset',
  description: 'Green particles with random scatter',
  primitives: [
    {
      type: 'circle',
      radius: 8,
      color: 0x00FF00,
      alpha: 0.5,
      depth: 93,
      tween: {
        alpha: 0,
        scale: 2,
        duration: 600,
        ease: 'Power2',
      },
      scatter: { count: 6, radius: 30 },
    },
    {
      type: 'circle',
      radius: 4,
      color: 0x006600,
      alpha: 0.7,
      depth: 94,
      tween: {
        y: -10,
        alpha: 0,
        scale: 0.5,
        duration: 500,
        ease: 'Sine.easeOut',
      },
      scatter: { count: 4, radius: 20 },
    },
  ],
};

const ARCANE_SPARKS: VFXEffectConfig = {
  id: 'preset_arcane_sparks',
  name: 'Arcane Sparks',
  category: 'particle_preset',
  description: 'Purple/magenta burst sparks',
  primitives: [
    {
      type: 'star',
      points: 4,
      innerRadius: 1,
      outerRadius: 4,
      color: 0xCC66FF,
      alpha: 0.9,
      depth: 96,
      tween: {
        alpha: 0,
        rotation: Math.PI * 0.5,
        scale: 0.3,
        duration: 250,
        ease: 'Power2',
      },
      scatter: { count: 10, radius: 40 },
    },
    {
      type: 'circle',
      radius: 10,
      color: 0xAA00FF,
      alpha: 0.3,
      depth: 93,
      tween: {
        alpha: 0,
        scale: 1.5,
        duration: 200,
        ease: 'Power1',
      },
    },
  ],
};

const BLOOD_SPLATTER: VFXEffectConfig = {
  id: 'preset_blood_splatter',
  name: 'Blood Splatter',
  category: 'particle_preset',
  description: 'Red droplets with gravity and quick fade',
  primitives: [
    {
      type: 'circle',
      radius: 4,
      color: 0xFF3333,
      alpha: 0.8,
      depth: 95,
      tween: {
        y: 20,
        alpha: 0,
        scale: 0.5,
        duration: 350,
        ease: 'Power2',
      },
      scatter: { count: 8, radius: 25 },
    },
    {
      type: 'circle',
      radius: 2,
      color: 0xCC0000,
      alpha: 0.9,
      depth: 96,
      tween: {
        y: 15,
        alpha: 0,
        duration: 300,
        ease: 'Quad.easeIn',
      },
      scatter: { count: 4, radius: 15 },
    },
  ],
};

const ELECTRIC_CRACKLE: VFXEffectConfig = {
  id: 'preset_electric_crackle',
  name: 'Electric Crackle',
  category: 'particle_preset',
  description: 'Yellow/white sparks with jitter',
  primitives: [
    {
      type: 'star',
      points: 4,
      innerRadius: 1,
      outerRadius: 5,
      color: 0xFFFFFF,
      alpha: 0.9,
      depth: 96,
      tween: {
        alpha: 0,
        rotation: Math.PI,
        scale: 0.3,
        duration: 150,
        ease: 'Power2',
      },
      scatter: { count: 8, radius: 35 },
    },
    {
      type: 'rect',
      width: 12,
      height: 1,
      color: 0xFFFF00,
      alpha: 0.8,
      depth: 95,
      tween: {
        alpha: 0,
        scaleX: 2,
        rotation: Math.PI * 0.25,
        duration: 180,
        ease: 'Power1',
      },
      scatter: { count: 5, radius: 25 },
    },
    {
      type: 'circle',
      radius: 8,
      color: 0x4488FF,
      alpha: 0.3,
      depth: 92,
      tween: {
        alpha: 0,
        scale: 1.8,
        duration: 250,
        ease: 'Power2',
      },
    },
  ],
};

// =============================================================================
// EXPORTS
// =============================================================================

export const PARTICLE_PRESETS: VFXEffectConfig[] = [
  FIRE_TRAIL,
  FROST_MIST,
  HOLY_RADIANCE,
  SHADOW_WISPS,
  POISON_CLOUD,
  ARCANE_SPARKS,
  BLOOD_SPLATTER,
  ELECTRIC_CRACKLE,
];
